// src/components/customers/JobSection.jsx
import { useState } from "react";

/**
 * JobSection Component
 *
 * A reusable component for displaying job information in organized sections.
 * Provides a consistent layout for different types of job data.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Section title
 * @param {string} props.icon - Optional icon name or SVG path
 * @param {React.ReactNode} props.children - Section content
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.collapsible - Whether the section can be collapsed
 * @param {boolean} props.defaultOpen - Default collapsed state (if collapsible)
 */
const JobSection = ({
  title,
  icon,
  children,
  className = "",
  collapsible = false,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleSection = () => {
    if (collapsible) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Section Header */}
      <div
        className={`flex items-center justify-between p-4 ${
          collapsible ? "cursor-pointer hover:bg-gray-50" : ""
        } ${title ? "border-b border-gray-200" : ""}`}
        onClick={toggleSection}
      >
        <div className="flex items-center">
          {/* Icon */}
          {icon && (
            <div className="mr-3">
              {typeof icon === "string" ? (
                <div
                  className="w-5 h-5 text-gray-500"
                  dangerouslySetInnerHTML={{ __html: icon }}
                />
              ) : (
                icon
              )}
            </div>
          )}

          {/* Title */}
          {title && (
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          )}
        </div>

        {/* Collapse/Expand Button */}
        {collapsible && (
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label={isOpen ? "Collapse section" : "Expand section"}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Section Content */}
      {(!collapsible || isOpen) && <div className="p-4">{children}</div>}
    </div>
  );
};

export default JobSection;
